import { useState, useEffect, useCallback } from 'react'
import { RequestService } from '../services/RequestService'
import { PLANS } from '../utils/planNames'
import { filterRows } from '../utils/filterRowsByTerm'

const translatePendingIntake = ({ value }) => {
  if (value === false) return 'completed'
  if (value) return 'pending'
  return null
}

const useVisualManagementGraph = ({ planName }) => {
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [resetRowsState, setResetRowsState] = useState([])
  const [filterOptions, setFilterOptions] = useState(null)
  const [selectedFilterOptions, setSelectedFilterOptions] = useState(null)
  const [inputValue, setInputValue] = useState('')
  const [selectedOrder, setSelectedOrder] = useState(
    'working,high_fatigue,sleep,prediction',
  )

  const handleType = e => {
    const inputValue = e.target.value
    setInputValue(inputValue)
    filterRows({
      filterByTerm: inputValue,
      rows: filterRowsByOptions(),
      defineRows: setRows,
    })
  }

  const transformEvaluation = ({ evaluation }) => {
    if (!evaluation) return { fatigueLevel: 0 }
    if (evaluation === 'good') return { fatigueLevel: 1 }
    if (evaluation === 'regular') return { fatigueLevel: 2 }
    if (evaluation === 'bad') return { fatigueLevel: 3 }
  }

  const generateInitialHeadState = ({ row }) => {
    let elements = {}

    for (let index = 0; index < 97; index++) {
      elements[`fatigueLevel8h${index}`] = {
        fatigueLevel: row.data[index]?.predictionResult,
        isSleeping: row.data[index]?.isSleeping,
        isWorking: row.data[index]?.isWorking,
      }
    }

    return elements
  }

  const createRows = ({ data }) => {
    const rows = data?.map(row => {
      const fatigues = generateInitialHeadState({ row })

      return {
        id: row.id,
        employee: row.colaborator,
        intakePending: translatePendingIntake({ value: row.intakePending }),
        highFatigue: row.timeTillAlert,
        notification: row.notificationStatus,
        counterMeasure: row.counterMeasureStatus,
        fatigueLevel8h:
          planName === PLANS.NOW &&
          transformEvaluation({ evaluation: row.data[0]?.prediction }),
        shift: row.shift,
        position: row.position.title,
        level: row.position.level,
        team: row.team,
        fatigueLevel: row.data[0]?.prediction,
        ...fatigues,
      }
    })

    setResetRowsState(rows)

    return rows || []
  }

  const filterRowsByOptions = () => {
    let rows = resetRowsState

    if (selectedFilterOptions?.fatigueLevels.length > 0) {
      rows = rows.filter(row =>
        selectedFilterOptions.fatigueLevels.includes(row.fatigueLevel),
      )
    }

    if (selectedFilterOptions?.shifts.length > 0) {
      rows = rows.filter(row =>
        selectedFilterOptions.shifts.includes(row.shift),
      )
    }

    if (selectedFilterOptions?.levels.length > 0) {
      rows = rows.filter(row =>
        selectedFilterOptions.levels.includes(row.level),
      )
    }

    if (selectedFilterOptions?.positions.length > 0) {
      rows = rows.filter(row =>
        selectedFilterOptions.positions.includes(row.position),
      )
    }

    if (selectedFilterOptions?.teams.length > 0) {
      rows = rows.filter(row => selectedFilterOptions.teams.includes(row.team))
    }

    if (selectedFilterOptions?.notificationsStatuses.length > 0) {
      rows = rows.filter(row =>
        selectedFilterOptions.notificationsStatuses.includes(row.notification),
      )
    }

    if (selectedFilterOptions?.counterMeasureStatuses.length > 0) {
      rows = rows.filter(row =>
        selectedFilterOptions.counterMeasureStatuses.includes(
          row.counterMeasure,
        ),
      )
    }

    return rows
  }

  const getPossibleFilters = ({ data }) => {
    const filters = {
      fatigueLevels: [],
      shifts: [],
      teams: [],
      positions: [],
      levels: [],
      notificationsStatuses: [],
      counterMeasureStatuses: [],
    }

    data.forEach(
      ({
        data,
        shift,
        team,
        position,
        notificationStatus,
        counterMeasureStatus,
      }) => {
        if (
          data[0]?.prediction &&
          !filters.fatigueLevels.includes(data[0]?.prediction)
        )
          filters.fatigueLevels.push(data[0].prediction)

        if (shift && !filters.shifts.includes(shift)) filters.shifts.push(shift)

        if (team && !filters.teams.includes(team)) filters.teams.push(team)

        if (position?.title && !filters.positions.includes(position.title))
          filters.positions.push(position.title)

        if (position?.level && !filters.levels.includes(position.level))
          filters.levels.push(position.level)

        if (
          notificationStatus &&
          !filters.notificationsStatuses.includes(notificationStatus)
        )
          filters.notificationsStatuses.push(notificationStatus)

        if (
          counterMeasureStatus &&
          !filters.counterMeasureStatuses.includes(counterMeasureStatus)
        )
          filters.counterMeasureStatuses.push(counterMeasureStatus)
      },
    )

    return filters
  }

  const onSelectOrderSave = ({ order }) => {
    const values = order.map(item => item.value)
    setSelectedOrder(values.join())
  }

  const fetchData = useCallback(async () => {
    setLoading(true)

    try {
      const response = await RequestService.getVisualManagementUsersData({
        orderBy: selectedOrder,
      })

      const data = response?.data?.data
      const rows = createRows({ data })
      const filters = getPossibleFilters({ data })

      setFilterOptions(filters)
      setRows(rows)
    } catch (e) {
      console.log('useVisualManagementGraph', e)
    }
    setLoading(false)
  }, [selectedOrder])

  useEffect(() => {
    fetchData()
  }, [selectedOrder])

  useEffect(() => {
    const filteredRows = filterRowsByOptions()
    setRows(filteredRows)
    setInputValue('')
  }, [selectedFilterOptions])

  return {
    loading,
    rows,
    filterOptions,
    setSelectedFilterOptions,
    inputValue,
    handleType,
    selectedFilterOptions,
    onSelectOrderSave,
  }
}

export default useVisualManagementGraph
